import { z } from 'zod';
import { paginationQuerySchema, uuidSchema } from './common.js';
/**
 * Stills and short clips the kiosk's Pi pulls from a machine's IP camera on
 * request. The Pi sits on the same LAN as the camera, so the server never
 * talks to it directly: it pushes a `capture_media` command down the Pi's
 * socket and the Pi uploads the result over HTTP.
 *
 * t1073 adds two kinds that come from the checkout *tablet* instead: `photo`
 * (its front-facing camera) and `screenshot` (what the checkout screen was
 * showing). Both are JPEGs. Staff can request one from the portal (delivered
 * on the tablet's next heartbeat), and the tablet can push a screenshot of
 * its own accord when checkout fails.
 */
export const PI_CAMERA_MEDIA_KINDS = ['snapshot', 'clip'];
export const TABLET_CAMERA_MEDIA_KINDS = ['photo', 'screenshot'];
export const CAMERA_MEDIA_KINDS = [...PI_CAMERA_MEDIA_KINDS, ...TABLET_CAMERA_MEDIA_KINDS];
export function isTabletCameraMediaKind(kind) {
    return TABLET_CAMERA_MEDIA_KINDS.includes(kind);
}
/**
 * Who answers a capture of this kind. The tablet kinds always go to the
 * tablet. A glass cabinet has no Pi, so its tablet also fetches the store's
 * IP-camera stills and clips (t1277); on a vending machine those stay with
 * the Pi.
 */
export function deliveredByTablet(kind, machineKind) {
    return isTabletCameraMediaKind(kind) || machineKind === 'cabinet';
}
/** First tablet app build (0.1.34) that answers `photo` and `screenshot`. */
export const TABLET_CAPTURE_MIN_VERSION_CODE = 35;
/** First tablet app build (0.1.35) that fetches IP-camera stills and clips
 *  for a glass cabinet and pushes automatic captures on a checkout error. */
export const TABLET_CAMERA_CAPTURE_MIN_VERSION_CODE = 36;
/** Who started a capture: staff from the portal, or the tablet itself after a
 *  checkout error. */
export const CAMERA_MEDIA_TRIGGERS = ['staff', 'checkout_error'];
/** `requested` is the pending-capture state; the other three are terminal. */
export const CAMERA_MEDIA_STATUSES = ['requested', 'ready', 'failed', 'timed_out'];
export const MAX_CAMERA_SNAPSHOT_BYTES = 5_000_000;
/** Ten seconds of 1080p H.264 is a few MB; this leaves room for 4K cameras
 *  while bounding what lands in Postgres and the nightly dump. */
export const MAX_CAMERA_CLIP_BYTES = 25_000_000;
export const MAX_CAMERA_CLIP_SECONDS = 30;
export const DEFAULT_CAMERA_CLIP_SECONDS = 10;
/** A `requested` capture with no upload after this long is reported as timed out. */
export const CAMERA_CAPTURE_TIMEOUT_SECONDS = 120;
/** A tablet only learns of a request on its next heartbeat (about a minute
 *  apart), so it gets longer than a Pi on a live socket. */
export const TABLET_CAPTURE_TIMEOUT_SECONDS = 300;
export const CAMERA_MEDIA_RETENTION_DAYS = 90;
export const cameraMediaSchema = z.object({
    id: uuidSchema,
    machineId: uuidSchema,
    machineName: z.string(),
    cameraId: uuidSchema.nullable(),
    cameraName: z.string(),
    kind: z.enum(CAMERA_MEDIA_KINDS),
    trigger: z.enum(CAMERA_MEDIA_TRIGGERS),
    /** The order a checkout-error capture belongs to, when the tablet knew it. */
    orderId: uuidSchema.nullable(),
    orderNumber: z.string().nullable(),
    status: z.enum(CAMERA_MEDIA_STATUSES),
    mimeType: z.string().nullable(),
    sizeBytes: z.number().int().nonnegative().nullable(),
    durationSeconds: z.number().int().nonnegative().nullable(),
    capturedAt: z.iso.datetime().nullable(),
    error: z.string().nullable(),
    requestedByName: z.string().nullable(),
    /** How many support tickets this item is attached to. */
    ticketCount: z.number().int().nonnegative(),
    createdAt: z.iso.datetime(),
});
export const requestCameraCaptureSchema = z
    .object({
    machineId: uuidSchema,
    /** Required for `snapshot` and `clip`; the tablet kinds have no camera row. */
    cameraId: uuidSchema.optional(),
    kind: z.enum(CAMERA_MEDIA_KINDS),
    /** Clips only; ignored for everything else. */
    durationSeconds: z
        .number()
        .int()
        .min(1)
        .max(MAX_CAMERA_CLIP_SECONDS)
        .default(DEFAULT_CAMERA_CLIP_SECONDS),
})
    .refine((v) => isTabletCameraMediaKind(v.kind) || v.cameraId !== undefined, {
    path: ['cameraId'],
    message: 'Choose a camera',
});
export const cameraMediaListQuerySchema = paginationQuerySchema
    .pick({ page: true, pageSize: true })
    .extend({
    machineId: uuidSchema.optional(),
    cameraId: uuidSchema.optional(),
    kind: z.enum(CAMERA_MEDIA_KINDS).optional(),
    trigger: z.enum(CAMERA_MEDIA_TRIGGERS).optional(),
    status: z.enum(CAMERA_MEDIA_STATUSES).optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
});
/**
 * What the server writes into a `capture_media` command's `params`, and what
 * the kiosk agent reads back out — `kioskServerMessageSchema` keeps `params`
 * a generic record, same as for `dispense`. `url` is the camera's snapshot
 * URL or RTSP URL and may carry a camera login, so neither side logs it.
 */
export const captureMediaParamsSchema = z.object({
    captureId: uuidSchema,
    kind: z.enum(CAMERA_MEDIA_KINDS),
    url: z.string().max(1000),
    durationSeconds: z.number().int().min(1).max(MAX_CAMERA_CLIP_SECONDS).optional(),
});
/**
 * What the server writes into a `capture_media` command's `params` when the
 * target is a tablet (best effort, only if the tablet happens to hold the
 * command socket — the heartbeat response is the reliable delivery path).
 * There is no `url`: the tablet captures with its own camera or screen.
 */
export const tabletCaptureMediaParamsSchema = z.object({
    captureId: uuidSchema,
    kind: z.enum(TABLET_CAMERA_MEDIA_KINDS),
});
/** The base64 text of a `MAX_CAMERA_SNAPSHOT_BYTES` image, with headroom. */
export const MAX_TABLET_CAPTURE_BASE64 = 6_700_000;
/**
 * Body of `POST /vending/terminal/camera-media` — the tablet pushing a capture
 * nobody asked for, because checkout just failed.
 *
 * Tablet contract (device bearer token, tablet role only):
 * - Only send when the heartbeat response's `errorCapture` flag for this kind
 *   is on; the server enforces the same setting and answers 403 otherwise.
 * - `imageBase64` is a JPEG of at most `MAX_CAMERA_SNAPSHOT_BYTES`, base64
 *   encoded without a data-URI prefix.
 * - `orderId` is the order being paid for, if the tablet knows it. A failure
 *   before the order exists has none — send null.
 * - Answers 201 `{ id }`. Do not retry a 4xx.
 */
export const tabletCaptureUploadSchema = z.object({
    kind: z.enum(TABLET_CAMERA_MEDIA_KINDS),
    trigger: z.literal('checkout_error'),
    orderId: uuidSchema.nullable().default(null),
    capturedAt: z.iso.datetime(),
    imageBase64: z.string().min(100).max(MAX_TABLET_CAPTURE_BASE64),
});
/**
 * Body of `POST /vending/terminal/camera-media/:id/failure` — the tablet could
 * not do a capture the portal asked for (no camera permission, camera busy…).
 * Shown to staff as the capture's error text.
 */
export const tabletCaptureFailureSchema = z.object({
    error: z.string().trim().min(1).max(500),
});
// --------------------------------------------------- support-ticket link
export const attachTicketMediaSchema = z.object({ mediaId: uuidSchema });
export const supportTicketMediaSchema = z.object({
    id: uuidSchema,
    ticketId: uuidSchema,
    attachedByName: z.string().nullable(),
    attachedAt: z.iso.datetime(),
    media: cameraMediaSchema,
});
//# sourceMappingURL=camera-media.js.map